import type { GeographyApi } from "@App/apis/graphql/geography/interfaces";
import type { Suggest } from "@App/apis/graphql/geography/responses/Suggest";
import { bus } from "@App/domain/bus";
import { EventType } from "@App/domain/events";
import { useGeoLocation } from "@App/hooks/useGeoLocation";
import { useStateDebounced } from "@App/hooks/useStateDebounced";
import { actionCreators, type State } from "@App/state";
import { Origin } from "@App/utils/analytics/origins";
import { bindActionCreators } from "@reduxjs/toolkit";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

type Suggestion = { id: string; name: string };

interface PlaceSearchOptions {
  disableFetchOnLoad?: boolean;
}

export const usePlaceSearch = (geographyApi: GeographyApi, options?: PlaceSearchOptions) => {
  const {
    isGeoLocationAvailable,
    isGeoLocationLoading,
    setIsGeoLocationLoading,
    handleGeoLocationSearch,
    error,
  } = useGeoLocationSearch(geographyApi, options);

  const [query, setQuery] = useStateDebounced("", 250);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [suggestions, setSuggestions] = useState<Suggest[] | null>(null);

  const place = useSelector((state: State) => state.orders.place);

  const dispatch = useDispatch();
  const { choosePlace } = bindActionCreators(actionCreators, dispatch);

  useEffect(() => {
    if (!query) return;

    setSuggestions(null);
    async function getResults() {
      const result = await geographyApi.getLocationSuggests(query);
      setSuggestions(result);
    }

    getResults();
  }, [query]);

  useEffect(() => {
    if (!selectedSuggestion) return;
    if (place && place.id === selectedSuggestion.id && place.lat && place.lon) return;

    choosePlace(null);
    setIsGeoLocationLoading(true);

    async function getExactMatch() {
      const result = await geographyApi.getLocationByID(selectedSuggestion.id);
      choosePlace(result);
      setIsGeoLocationLoading(false);
    }

    getExactMatch();
  }, [selectedSuggestion]);

  return {
    query,
    setQuery,
    selectedSuggestion,
    setSelectedSuggestion,
    suggestions,
    handleGeoLocationSearch,
    isGeoLocationAvailable,
    isGeoLocationLoading,
    isSubmitDisabled: selectedSuggestion === null,
    error,
  };
};

interface GeoLocationSearchOptions {
  disableFetchOnLoad?: boolean;
}

export const useGeoLocationSearch = (
  geographyApi: GeographyApi,
  options?: GeoLocationSearchOptions,
) => {
  const { isGeoLocationAvailable, getGeoLocation } = useGeoLocation();
  const [isGeoLocationLoading, setIsGeoLocationLoading] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch();
  const { choosePlace } = bindActionCreators(actionCreators, dispatch);

  const place = useSelector((state: State) => state.orders.place);
  const organization = useSelector((state: State) => state.orders.organization);

  const eventPayLoad = {
    organization: {
      id: organization.id,
      slug: organization.slug,
      name: organization.name,
    },
    origin: Origin.WLV1,
  };

  const fetched = useRef(false);

  // try to get the geolocation once on page load
  useEffect(() => {
    async function fetchGeoLocation() {
      if (fetched.current) return;
      fetched.current = true;
      await handleGeoLocationSearch();
    }

    if (options?.disableFetchOnLoad) return;
    if (!place) fetchGeoLocation();
  }, []);

  const handleGeoLocationSearch = async () => {
    setError(null);

    let position: GeolocationPosition;
    try {
      position = await getGeoLocation();
      bus.publish({
        type: EventType.GEOLOCATION_GRANTED,
        ...eventPayLoad,
      });
    } catch (e) {
      if (e instanceof GeolocationPositionError && e.code == e.PERMISSION_DENIED) {
        bus.publish({
          type: EventType.GEOLOCATION_DENIED,
          ...eventPayLoad,
        });
      } else {
        setError(t("organisms.common.placeSearchInput.geoLocation.errors.lookupFailed"));
        bus.publish({
          type: EventType.GEOLOCATION_FAILED,
          ...eventPayLoad,
          err: e,
        });
      }
    }

    if (!position) return;

    setIsGeoLocationLoading(true);

    try {
      const locationPromise = geographyApi.getLocationByLatLon(
        position.coords.latitude,
        position.coords.longitude,
      );
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Geolocation lookup timed out")), 10000),
      );
      const location = await Promise.race([locationPromise, timeoutPromise]);
      choosePlace(location);

      bus.publish({
        type: EventType.GEOLOCATION_COMPLETED,
        ...eventPayLoad,
      });
    } catch (e) {
      setError(t("organisms.common.placeSearchInput.geoLocation.errors.lookupFailed"));
      bus.publish({
        type: EventType.GEOLOCATION_FAILED,
        ...eventPayLoad,
        err: e,
      });
    }

    setIsGeoLocationLoading(false);
  };

  return {
    handleGeoLocationSearch,
    isGeoLocationAvailable,
    isGeoLocationLoading,
    setIsGeoLocationLoading,
    error,
  };
};
